import React, { FC } from 'react';
import { Link } from 'react-router-dom';
import { ADMIN_ROUTES } from 'constants/routes';
import { CloseOutlined } from '@ant-design/icons';
import { usePurchaseOrderItemPurchasePriceListQuery } from 'api';
import { Button, Col, Divider, Drawer, Row, SupplierStatus, Tag, Typography } from 'components';
import moment from 'moment/moment';
import { ChartIcon } from 'components/common/icons';
import styles from './../../../styles.module.scss';
import { HistoricalDataModal } from './historicalDataModal';
import { SUPPLIER_OFFER_SOURCE, SUPPLIER_OFFER_TYPE, SupplierOfferType } from 'models/SupplierOffer';

const { Text } = Typography;

interface IPurchasePricesDrawer {
  onExpand: () => void;
  drawerState: boolean;
  data?: string;
}

export const PurchasePricesDrawer: FC<IPurchasePricesDrawer> = React.memo((props) => {
  const { onExpand, drawerState, data } = props;
  const [historicalDataModalOpen, setHistoricalDataModalOpen] = React.useState(false);

  const { data: purchaseOrderPriceData = {} } = usePurchaseOrderItemPurchasePriceListQuery({ sku: data });
  const { supplierOffersResponses = [], itemSku, itemId } = purchaseOrderPriceData || {};

  const handleHistoricalDataClick = () => {
    setHistoricalDataModalOpen(true);
  };

  const handleHistoricalDataClose = () => {
    setHistoricalDataModalOpen(false);
  };

  return (
    <Drawer
      closeIcon={null}
      placement="left"
      width={471}
      open={drawerState}
      onClose={() => onExpand()}
      styles={{
        header: {
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        },
      }}
      title={'Purchase Price'}
    >
      <div style={{ position: 'absolute', right: 16, top: 16 }}>
        <Button icon={<CloseOutlined />} onClick={() => onExpand()} type="text" />
      </div>
      <Row gutter={[0, 16]}>
        <Col span={24} className={styles.item_sku_box}>
          <Row align={'middle'} justify={'space-between'}>
            <Col>
              <Text className={'font-size-16 font-weight-500'}>Item SKU - </Text>
              <Link to={`${ADMIN_ROUTES.items.view}/${itemId}/suppliers`} target={'_blank'}>
                <Text underline className={'font-size-16 font-weight-500'} ellipsis={{ tooltip: true }}>
                  {itemSku}
                </Text>
              </Link>
            </Col>
            <Col>
              <Row className={'gap-4'} align={'middle'}>
                <ChartIcon />
                <Text
                  className={'pointer font-weight-500'}
                  style={{ color: '#05796B' }}
                  onClick={handleHistoricalDataClick}
                >
                  Historical Data
                </Text>
              </Row>
            </Col>
          </Row>
        </Col>
        <Col span={24}>
          <Row gutter={[0, 30]}>
            {supplierOffersResponses?.map((_item: Record<string, any>, index: number) => {
              return (
                <Col span={24} key={_item.supplierOfferId}>
                  <Row gutter={[0, 8]}>
                    <Col span={24}>
                      <Row align={'middle'} justify={'space-between'}>
                        <Col>
                          <Row className={'gap-6'} align={'middle'}>
                            <Col>
                              <Link
                                to={`${ADMIN_ROUTES.suppliers.view}/${_item.supplier.id}/general`}
                                target={'_blank'}
                              >
                                <Text underline className={'font-size-16 font-weight-600'} ellipsis={{ tooltip: true }}>
                                  {_item.supplier.name}
                                </Text>
                              </Link>
                            </Col>
                            {_item.lastOrderedDate && (
                              <Col className={'date-tag'}>
                                <Text type={'secondary'} className={'font-weight-500 font-size-12'}>
                                  {index === 0
                                    ? `Last ordered ${moment(_item.lastOrderedDate).format('DD.MM.YYYY')}`
                                    : `Previously ordered ${moment(_item.lastOrderedDate).format('DD.MM.YYYY')}`}
                                </Text>
                              </Col>
                            )}
                          </Row>
                        </Col>
                        <Col>
                          <Text
                            className={`${_item.supplier.mainSupplier ? 'main-supplier-tag' : _item.supplier.priceReferenceSupplier ? 'price-reference-supplier-tag' : ''}`}
                          >
                            {_item.supplier.mainSupplier ? (
                              'Main Supplier'
                            ) : _item.supplier.priceReferenceSupplier ? (
                              'Price Reference Supplier'
                            ) : ['BLACKLISTED', 'INACTIVE'].includes(_item.supplier.status) ? (
                              <SupplierStatus status={_item.supplier.status} />
                            ) : null}
                          </Text>
                        </Col>
                      </Row>
                    </Col>
                    {!!_item.supplier.tags?.length && (
                      <Col span={24}>
                        <Row className={'gap-4'}>
                          {_item.supplier.tags.map((_tag: string, index: number) => (
                            <Tag title={_tag} key={index + _tag} className={'custom-tag'}>
                              {_tag}
                            </Tag>
                          ))}
                        </Row>
                      </Col>
                    )}
                    <Col span={24}>
                      <Text type={'secondary'}>
                        <Text className={'font-weight-500'}>Additional Notes:</Text>
                        {_item.supplier.additionalNotes}
                      </Text>
                    </Col>
                    <Divider className={styles.divider} />
                    <Col span={24}>
                      <Row>
                        <Col span={24}>
                          <Row gutter={[12, 12]} justify={'space-between'} wrap={false}>
                            <Col flex="none">
                              <Text type={'secondary'} style={{ whiteSpace: 'nowrap' }}>
                                Supplier Offer Number
                              </Text>
                            </Col>
                            <Col flex="auto" style={{ textAlign: 'right' }}>
                              {!!_item.supplierOfferId && (
                                <Link
                                  to={ADMIN_ROUTES.supplierOffer.supplierOfferUpdate
                                    .replace(':type', SUPPLIER_OFFER_TYPE[_item.supplierOfferType as SupplierOfferType])
                                    .replace(':source', SUPPLIER_OFFER_SOURCE.MAIN)
                                    .replace(':supplierOfferId', _item.supplierOfferId)}
                                  target={'_blank'}
                                >
                                  <Text underline type={'secondary'} ellipsis={{ tooltip: true }}>
                                    {_item.supplierOfferUuid}
                                  </Text>
                                </Link>
                              )}
                            </Col>
                          </Row>
                        </Col>
                        <Col span={24}>
                          <Row gutter={[12, 12]} justify={'space-between'} wrap={false}>
                            <Col flex="none">
                              <Text type={'secondary'} style={{ whiteSpace: 'nowrap' }}>
                                Validity Date
                              </Text>
                            </Col>
                            <Col flex="auto" style={{ textAlign: 'right' }}>
                              <Text type={'secondary'} ellipsis={{ tooltip: true }}>
                                {_item.validityDate ? moment.utc(_item.validityDate).format('DD.MM.YYYY') : 'Not Set'}
                              </Text>
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                    </Col>
                    <Divider className={styles.divider} />
                    <Col span={24}>
                      <Row>
                        <Col span={24}>
                          <Row gutter={[12, 12]} justify={'space-between'} wrap={false}>
                            <Col flex="none">
                              <Text type={'secondary'} style={{ whiteSpace: 'nowrap' }}>
                                Qty
                              </Text>
                            </Col>
                            <Col flex="auto" style={{ textAlign: 'right' }}>
                              <Text type={'secondary'} ellipsis={{ tooltip: true }}>
                                {_item.quantity || 'Not Set'}
                              </Text>
                            </Col>
                          </Row>
                        </Col>
                        <Col span={24}>
                          <Row gutter={[12, 12]} justify={'space-between'} wrap={false}>
                            <Col flex="none">
                              <Text type={'secondary'} style={{ whiteSpace: 'nowrap' }}>
                                Purchase price per piece
                              </Text>
                            </Col>
                            <Col flex="auto" style={{ textAlign: 'right' }}>
                              <Text type={'secondary'} ellipsis={{ tooltip: true }}>
                                {_item.purchasePricePerPiece ? `€${_item.purchasePricePerPiece}` : 'Not Set'}
                              </Text>
                            </Col>
                          </Row>
                        </Col>
                        <Col span={24}>
                          <Row gutter={[12, 12]} justify={'space-between'} wrap={false}>
                            <Col flex="none">
                              <Text type={'secondary'} style={{ whiteSpace: 'nowrap' }}>
                                Discount
                              </Text>
                            </Col>
                            <Col flex="auto" style={{ textAlign: 'right' }}>
                              <Text type={'secondary'} ellipsis={{ tooltip: true }}>
                                {_item.discount ? `${_item.discount}%` : 'Not Set'}
                              </Text>
                            </Col>
                          </Row>
                        </Col>
                        <Col span={24}>
                          <Row gutter={[12, 12]} justify={'space-between'} wrap={false}>
                            <Col flex="none">
                              <Text type={'secondary'} style={{ whiteSpace: 'nowrap' }}>
                                Shipping cost
                              </Text>
                            </Col>
                            <Col flex="auto" style={{ textAlign: 'right' }}>
                              <Text type={'secondary'} ellipsis={{ tooltip: true }}>
                                {_item.shippingCost ? `€${_item.shippingCost}` : 'Not Set'}
                              </Text>
                            </Col>
                          </Row>
                        </Col>
                        <Col span={24}>
                          <Row gutter={[12, 12]} justify={'space-between'} wrap={false}>
                            <Col flex="none">
                              <Text type={'secondary'} style={{ whiteSpace: 'nowrap' }}>
                                Fees
                              </Text>
                            </Col>
                            <Col flex="auto" style={{ textAlign: 'right' }}>
                              <Text type={'secondary'} ellipsis={{ tooltip: true }}>
                                {_item.fees ? `€${_item.fees}` : 'Not Set'}
                              </Text>
                            </Col>
                          </Row>
                        </Col>
                        <Col span={24}>
                          <Row gutter={[12, 12]} justify={'space-between'} wrap={false}>
                            <Col flex="none">
                              <Text className={'font-weight-600'} style={{ whiteSpace: 'nowrap' }}>
                                Purchase Price
                              </Text>
                            </Col>
                            <Col flex="auto" style={{ textAlign: 'right' }}>
                              <Text className={'font-weight-600'} ellipsis={{ tooltip: true }}>
                                {_item.totalPurchasePrice ? `€${_item.totalPurchasePrice}` : 'Not Set'}
                              </Text>
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </Col>
              );
            })}
          </Row>
        </Col>
      </Row>

      <HistoricalDataModal
        open={historicalDataModalOpen}
        onClose={handleHistoricalDataClose}
        itemSku={itemSku}
        initialSupplierId={supplierOffersResponses?.[0]?.supplier?.id}
      />
    </Drawer>
  );
});
